import { InstallmentPlan, MAX_TOTAL_INSTALLMENTS } from './types';
import { capitalize } from 'core/utils';

export const DEPOSIT_TYPE_OPTIONS = [
  { value: 'first_installment', text: 'First Installment' },
  { value: 'percentage', text: '% of Total Fees' },
  { value: 'amount', text: 'Other Amount' },
];

export const DEPOSIT_TYPE_SELECT_OPTIONS = [
  { value: 'first_installment', label: 'First installment' },
  { value: 'percentage', label: '% of total fee' },
  { value: 'amount', label: 'Custom amount' },
];

export const FREQUENCY_TYPE_SELECT_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
];

export const WEEKLY_OPTIONS = [
  { value: 'monday', text: 'Monday' },
  { value: 'tuesday', text: 'Tuesday' },
  { value: 'wednesday', text: 'Wednesday' },
  { value: 'thursday', text: 'Thursday' },
  { value: 'friday', text: 'Friday' },
  { value: 'saturday', text: 'Saturday' },
  { value: 'sunday', text: 'Sunday' },
];

export const WEEKLY_SELECT_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export const INSTALLMENT_PLAN_LABELS = {
  name: 'Plan Name',
  depositType: 'Due at checkout',
  depositAmount: 'Deposit Amount',
  installmentCount: 'Number of installments',
  frequency: 'Frequency',
  withdrawDay: 'Installment processes on',
  withdrawBasedOnCheckout: 'Based on checkout date',
};

export const FREQUENCY_MAP = {
  weekly_1: 'Weekly',
  weekly_2: 'Every 2 weeks',
  monthly_1: 'Monthly',
};

export const MONTHLY_OPTIONS = Array.from(Array(31).keys()).map((n) => ({
  text: `${n + 1}`,
  value: `${n + 1}`,
}));

export const BLANK_OPTION = { value: '', label: '' };

export const INSTALLMENT_COUNT_OPTIONS = Array.from(
  Array(MAX_TOTAL_INSTALLMENTS).keys()
).map((n) => ({ value: n + 1, label: n + 1 }));

export const isMonthly = (value) => value?.includes('monthly');

export const getFrequencyOptions = () =>
  Object.entries(FREQUENCY_MAP).map(([value, text]) => ({ value, text })) as {
    text: string;
    value: string;
  }[];

export const joinFrequencyValues = (frequencyType, frequencyAmount) => {
  const freqKey = `${frequencyType}_${frequencyAmount || 1}`;
  return FREQUENCY_MAP[freqKey] ? freqKey : undefined;
};

export const splitFrequencyValues = (
  frequency
): { frequencyType?: string; frequencyAmount?: number } => {
  if (frequency) {
    const parts = frequency.split('_');
    const frequencyType = parts[0];
    const frequencyAmount = parseInt(parts[1] || '1');
    return { frequencyType, frequencyAmount };
  }
  return {};
};

const COUNT_STRINGS = {
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '5': 'fifth',
  '6': 'sixth',
  '7': 'seventh',
  '8': 'eighth',
  '9': 'ninth',
  '10': 'tenth',
  '11': 'eleventh',
  '12': 'twelfth',
  '13': 'thirteenth',
  '14': 'fourteenth',
  '15': 'fifteenth',
  '16': 'sixteenth',
  '17': 'seventeenth',
  '18': 'eighteenth',
  '19': 'nineteenth',
  '20': 'twentieth',
  '21': 'twenty first',
  '22': 'twenty second',
  '23': 'twenty third',
  '24': 'twenty fourth',
  '25': 'twenty fifth',
  '26': 'twenty sixth',
  '27': 'twenty seventh',
  '28': 'twenty eighth',
  '29': 'twenty ninth',
  '30': 'thirtieth',
  '31': 'thirty first',
  '32': 'thirty second',
  '33': 'thirty third',
  '34': 'thirty fourth',
  '35': 'thirty fifth',
  '36': 'thirty sixth',
  '37': 'thirty seventh',
  '38': 'thirty eighth',
  '39': 'thirty ninth',
  '40': 'fortieth',
  '41': 'forty first',
  '42': 'forty second',
  '43': 'forty third',
  '44': 'forty fourth',
  '45': 'forty fifth',
  '46': 'forty sixth',
  '47': 'forty seventh',
  '48': 'forty eighth',
};

export const formatDayOfMonth = (day: string): string => {
  if (['13', '11'].includes(day)) {
    return `${day}th`;
  }
  if (['2', '22'].includes(day)) {
    return `${day}nd`;
  }
  switch (day.split('').pop()) {
    case '1':
      return `${day}st`;
    case '3':
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};
export const MONTHLY_SELECT_OPTIONS = Array.from(Array(31).keys()).map((n) => ({
  value: `${n + 1}`,
  label: formatDayOfMonth(`${n + 1}`),
}));

export const formatToDollarAmount = (amount) =>
  amount ? (amount / 100).toFixed(2).toString() : amount;
export const formatToPercentageAmount = (amount) =>
  amount ? (amount / 100).toString() : amount;

export const getInstallmentsString = (installments) =>
  `${installments} equal payments`;
export const getFrequencyString = (frequency) =>
  (FREQUENCY_MAP[frequency] || '').toLowerCase();

export const getWithdrawString = (frequency, withdrawDay) => {
  if (isMonthly(frequency)) {
    return `on the ${formatDayOfMonth(withdrawDay)}`;
  }
  return `on ${capitalize(withdrawDay)}`;
};

// build the natural language description of the plan from the plan object
export const getDepositDescription = (
  { depositType, depositAmount }: Partial<InstallmentPlan>,
  currencySymbol: string = '$'
) => {
  switch (depositType) {
    case 'amount':
      return `${currencySymbol}${formatToDollarAmount(depositAmount)}`;
    case 'percentage':
      return `${formatToPercentageAmount(depositAmount)}%`;
    case 'first_installment':
      return 'First installment';
    default:
      return '';
  }
};

export const getInstallmentsDescription = ({
  depositType,
  depositAmount,
  installmentCount,
  frequencyType,
  frequencyAmount,
  withdrawBasedOnCheckout,
  withdrawDay,
  scheduledDates,
}: Partial<InstallmentPlan>) => {
  const isCustomFrequency = frequencyType === 'custom';
  const numberOfScheduledDates = scheduledDates?.length || 0;
  const numberOfInstallments = isCustomFrequency
    ? numberOfScheduledDates + (depositType === 'first_installment' ? 1 : 0)
    : installmentCount;

  let installmentsString = `${numberOfInstallments} installment${numberOfInstallments > 1 ? 's' : ''}`;
  if (isCustomFrequency) {
    return `${installmentsString}, custom payment schedule`;
  }
  let withdrawString = '';
  if (depositType === 'first_installment') {
    const paymentPercentage = Math.round(100 / numberOfInstallments);
    installmentsString = `${paymentPercentage}% installments`;
  }
  if (depositType === 'percentage') {
    const paymentPercentage = Math.round(
      (100 - depositAmount / 100) / numberOfInstallments
    );
    installmentsString = `${paymentPercentage}% installments`;
  }
  const frequency = joinFrequencyValues(frequencyType, frequencyAmount);
  const frequencyString = getFrequencyString(frequency);
  if (!withdrawBasedOnCheckout) {
    withdrawString = getWithdrawString(frequency, withdrawDay);
  }
  return `${installmentsString}, ${frequencyString} ${withdrawString}`;
};

// build the natural language description of the plan from the form values
export const getPlanSummary = ({
  depositType,
  depositAmount,
  installmentCount,
  frequency,
  withdrawDay,
  withdrawBasedOnCheckout,
  currencySymbol,
}: any) => {
  if (!depositType || !installmentCount || !frequency) {
    return '';
  }

  // how much down payment
  const depositString = getDepositDescription(
    { depositType, depositAmount: depositAmount * 100 },
    currencySymbol
  );

  // how many installments
  const installmentString = getInstallmentsString(
    depositType === 'first_installment'
      ? installmentCount - 1
      : installmentCount
  );

  // how often and what day payments are made
  const frequencyString = getFrequencyString(frequency);
  const withdrawString =
    withdrawBasedOnCheckout === 'yes'
      ? ''
      : getWithdrawString(frequency, withdrawDay);

  return `${depositString}, then ${installmentString} ${frequencyString} ${withdrawString}`;
};

export const getDateText = (
  installmentNumber: number,
  plan: Partial<InstallmentPlan>
): string => {
  if (installmentNumber === 0) {
    return 'Checkout date';
  }
  if (plan.frequencyType === 'weekly') {
    const dayText = plan.withdrawBasedOnCheckout
      ? 'week'
      : capitalize(plan.withdrawDay);
    return `${capitalize(
      COUNT_STRINGS[installmentNumber * plan.frequencyAmount]
    )} ${dayText} after checkout`;
  }
  if (plan.frequencyType === 'monthly') {
    const dayText = plan.withdrawBasedOnCheckout
      ? ''
      : `${formatDayOfMonth(plan.withdrawDay)} of the `;
    return capitalize(
      `${dayText}${COUNT_STRINGS[installmentNumber]} month after checkout`
    );
  }
  return '';
};

export const formatPercentage = (percent: number): number =>
  percent % 1 === 0 ? percent : parseFloat(percent.toFixed(2));

export const getPaymentSummary = (plan: Partial<InstallmentPlan>): string => {
  const depositIsFirstInstallment = plan.depositType === 'first_installment';
  const depositIsPercentage = plan.depositType === 'percentage';
  const percentage = depositIsPercentage ? 100 - plan.depositAmount / 100 : 100;
  let installmentCount = plan.installmentCount;
  if (plan.frequencyType === 'custom') {
    installmentCount = plan.scheduledDates.length + (depositIsFirstInstallment ? 1 : 0);
	}  
  const installmentPercent = percentage / installmentCount;
  const percentDisplay = formatPercentage(installmentPercent);
  return `${percentDisplay}% of ${
    depositIsFirstInstallment || depositIsPercentage
      ? 'total'
      : 'the remaining balance'
  }`;
};
